import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { copyToClipboard, selectAllOnClick } from '../utils/clipboard';

/**
 * The one and only time a user API key secret is visible (#1115).
 *
 * The server stores an HMAC, so there is no "show it again" — losing this means revoking and
 * creating another. The dialog is therefore deliberately awkward to dismiss by accident: no
 * backdrop close, no Escape, and a confirmation if nothing was copied.
 *
 * Key id and secret are shown as SEPARATE fields as well as combined: `curl -u` wants the pair, but
 * a user pasting into a config file usually needs the two halves independently, and splitting a
 * single blob on the wrong character is an easy mistake to make.
 */
const CopyField = ({ label, value, helperText, onCopied }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    setCopied(ok);
    if (ok) onCopied?.();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <TextField
        label={label}
        value={value}
        helperText={helperText}
        fullWidth
        multiline
        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        onClick={selectAllOnClick}
      />
      <Tooltip title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
        <IconButton onClick={handleCopy} sx={{ mt: 1 }} aria-label={`Copy ${label}`}>
          <CopyIcon fontSize="small" color={copied ? 'success' : 'inherit'} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ApiKeyRevealDialog = ({ open, apiKey, onClose }) => {
  const [copiedAnything, setCopiedAnything] = useState(false);

  if (!apiKey) return null;

  const handleClose = () => {
    if (!copiedAnything) {
      // eslint-disable-next-line no-alert
      const proceed = window.confirm(
        'You have not copied the secret. It cannot be shown again — close anyway?'
      );
      if (!proceed) return;
    }
    setCopiedAnything(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      // Dismissing this by clicking away loses the secret for good.
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
    >
      <DialogTitle>API key created</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Copy this now.</strong> For security the secret is stored only as a hash, so it
          cannot be shown again. If you lose it, revoke this key and create another.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CopyField
            label="Key ID"
            value={apiKey.key_id}
            helperText="The username half of HTTP Basic. Safe to store in plain sight."
            onCopied={() => setCopiedAnything(true)}
          />
          <CopyField
            label="Secret"
            value={apiKey.secret}
            helperText="The password half. Treat it like a password — it acts as you."
            onCopied={() => setCopiedAnything(true)}
          />
          <CopyField
            label="Try it"
            value={apiKey.curl_example}
            helperText="Use https:// directly — an HTTP redirect drops the request body on some clients."
            onCopied={() => setCopiedAnything(true)}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          This key acts with your permissions
          {apiKey.read_only ? ' but can only read (GET requests)' : ''}. It cannot sign in, change
          your password, or manage API keys.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyRevealDialog;
